import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";

export default function MasterLogin() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const auth = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (auth?.status === "auth" && auth.user?.role === "master_admin") {
      nav("/master/dashboard", { replace: true });
    }
  }, [auth?.status, auth?.user?.role, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await auth.login({ email: email.trim().toLowerCase(), password: pw, tenant_slug: null });
      nav("/master/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: Brand pane */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 swiss-grid opacity-[0.07]" />
        <div className="relative flex items-center gap-3">
          <img src="/sua-plataforma-logo.png" alt="SUA PLATAFORMA" className="w-10 h-10 rounded-lg bg-background/95 p-1 object-contain shrink-0" />
          <div className="font-display font-bold text-xl">SUA PLATAFORMA</div>
        </div>
        <div className="relative max-w-md space-y-4">
          <div className="text-xs uppercase tracking-[0.25em] opacity-60">Painel da Matriz</div>
          <h1 className="font-display text-4xl font-bold leading-tight">
            Operação, governança e expansão da sua plataforma.
          </h1>
          <p className="text-base opacity-80 leading-relaxed">
            Gerencie empresas, recursos, modelos prontos, marca e histórico de alterações em uma única central.
          </p>
        </div>
        <div className="relative text-xs opacity-50 tracking-wider uppercase">Central de Crescimento</div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <Card className="w-full max-w-md p-8 rounded-2xl border-border bg-card">
          <div className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="w-4 h-4" /> Acesso restrito · Administrador da matriz
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-1">Entrar no Painel da Matriz</h2>
          <p className="text-sm text-muted-foreground mb-8">Use sua credencial de super administrador.</p>

          <form onSubmit={submit} className="space-y-5" data-testid={TID.masterLoginForm}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@suaplataforma.io"
                required
                className="h-11"
                data-testid={TID.masterLoginEmail}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw">Senha</Label>
              <Input
                id="pw"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                className="h-11"
                data-testid={TID.masterLoginPassword}
              />
            </div>
            {err && (
              <div data-testid={TID.masterLoginError} className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid={TID.masterLoginSubmit}
              className="w-full h-11 gap-2 font-medium"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Acessar Painel da Matriz
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
